/** The table page's one line of filters - ADR-0212 decision 2.
 *
 * It is `FilterBar` on a single row that does not wrap: the **table's own search box first** (the
 * global search is the palette; this one searches this table only), then the filters - single-choice
 * ones as styled native selects, multi-choice ones as chips - then **Reset, only while a filter is
 * active**, and at the right end the count, the page's actions and the **Charts toggle**. The search
 * takes the room that is left, so the line is one row at 1024px. The count is the server's total,
 * printed (`resultCount`), never computed here, so the line cannot disagree with the table about how
 * many rows there are.
 *
 * **Power features are opt-in** (`power`, from `useGridPower`): the saved-view tabs go above the line, the
 * controls become an "Add filter" button and removable chips (`chips`, on by default with `power`), the
 * count reads "38 of 214", and the tools (group, density, columns, export, shortcuts) join the right
 * end. While any row is ticked the line is **replaced** by the bulk bar - "3 selected | actions |
 * Clear". Without `power` it is the plain line it always was.
 *
 * The Charts toggle is drawn when the `TablePage` around it has charts to open (`ChartsContext`); a
 * line used on its own, or on a page with no charts, has none.
 */

import { CaretDown } from "@phosphor-icons/react";
import { useContext } from "react";

import { ChartsContext } from "./charts";
import type { FilterBarProps } from "./FilterBar";
import { FilterBar } from "./FilterBar";
import styles from "./FilterLine.module.css";
import { EMPTY_FILTERS } from "./filters";
import type { FilterDef, FilterState } from "./filters";
import { BulkBar } from "./tablePower/BulkBar";
import { TableTools } from "./tablePower/TableTools";
import type { GridPower } from "./tablePower/useGridPower";
import { ViewTabs } from "./tablePower/ViewTabs";

export type FilterLineProps = Omit<
  FilterBarProps,
  "singleLine" | "nativeSelects" | "searchLabel" | "filters" | "state" | "onChange" | "chipLine" | "filterRequest"
> & {
  /** The search box's name. Defaults to "Search this table". */
  searchLabel?: string;
  /** Default: the power state's. */
  filters?: readonly FilterDef[];
  state?: FilterState;
  onChange?: (next: FilterState) => void;
  /** Filters as an "Add filter" button and chips. Default: on when `power` is given. */
  chips?: boolean;
  power?: GridPower;
};

export function FilterLine({
  searchLabel = "Search this table",
  testId = "filter-line",
  actions,
  filters,
  state,
  onChange,
  chips,
  power,
  resultCount,
  ...rest
}: FilterLineProps) {
  const charts = useContext(ChartsContext);
  const toggle =
    charts?.available === true ? (
      <button
        type="button"
        className={styles.charts}
        aria-expanded={charts.open}
        data-testid="charts-toggle"
        onClick={charts.toggle}
      >
        Charts
        <CaretDown size={12} weight="bold" aria-hidden="true" className={charts.open ? styles.up : undefined} />
      </button>
    ) : null;
  const bulk = power !== undefined && power.selectedCount > 0;
  return (
    <div className={styles.line} data-filter-line data-bulk={bulk || undefined}>
      {power ? <ViewTabs power={power} /> : null}
      {bulk ? (
        <BulkBar power={power} />
      ) : (
        <FilterBar
          {...rest}
          filters={filters ?? power?.filterDefs ?? []}
          state={state ?? power?.filters ?? EMPTY_FILTERS}
          onChange={onChange ?? power?.setFilters ?? (() => undefined)}
          resultCount={resultCount ?? undefined}
          searchLabel={searchLabel}
          testId={testId}
          singleLine
          nativeSelects
          chipLine={chips ?? power !== undefined}
          filterRequest={power?.filterRequest}
          actions={
            <>
              {actions}
              {power ? <TableTools power={power} /> : null}
              {toggle}
            </>
          }
        />
      )}
    </div>
  );
}
